import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * Service for managing the JWT token in local storage and checking session status.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  localStorageJwtKey = 'BCS_JWT';

  token: string | undefined = '';

  validityTime = 8 * 60 * 60 * 1000; // 8 hour in milliseconds
  tenMin = 10 * 60000; // 10 min in mls
  fiveMin = 5 * 60000; // 5 min in mls
  oneMin = 60000; // 1 min in mls

  timeout!: NodeJS.Timeout;

  constructor(private authService: AuthService) {}

  /**
   * Retrieves the JWT token from local storage, if it exists.
   * If the token has already been retrieved, returns the cached value.
   * @returns The JWT token, or null if it does not exist in local storage.
   */
  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem(this.localStorageJwtKey) || '';
    }

    return this.token;
  }

  /**
   * Gets the authentication time in milliseconds since the Unix epoch.
   * If available, uses the `auth_time` claim from the Azure AD ID token.
   * @returns {number} The authentication time in milliseconds since the Unix epoch.
   */
  get auth_time() {
    let auth_time = new Date().getTime() - this.validityTime;

    if (this.authService.azureUser?.idTokenClaims?.auth_time) {
      auth_time = this.authService.azureUser?.idTokenClaims?.auth_time * 1000;
    }

    return auth_time;
  }

  /**
   * Gets the expiration time of the authentication token.
   * If the authentication time is set, adds the validity time to it.
   * @returns The expiration time of the authentication token.
   */
  get expired_time() {
    let expired_time = new Date().getTime();

    if (this.auth_time) {
      expired_time = new Date(this.auth_time).getTime() + this.validityTime;
    }

    return expired_time;
  }

  /**
   * Returns the time left until the token expires, in milliseconds.
   * @returns {number} The time left until the token expires, in milliseconds.
   */
  get time_left() {
    return this.expired_time - new Date().getTime();
  }

  /**
   * Checks if the access token has expired and logs out the user if it has.
   */
  checkAccessToWeb() {
    const exp_tkn_time = localStorage.getItem('exp_tkn_time');

    if (exp_tkn_time && Number(exp_tkn_time) <= new Date().getTime()) {
      this.authService.logout();
    }
  }
}
